// Local-first RGPD gamification. All state persisted in localStorage — no backend needed.
// Points are awarded for engaging with the RGPD content (reading, chatbot, certificate).
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'fdn_rgpd_state_v1';

export const BADGES = [
  { id: 'video-watched', label: 'Lecteur attentif', points: 10, icon: 'BookOpen', hint: 'Ouvrez la charte pendant 30 secondes.' },
  { id: 'chatbot-used', label: 'Assistant Sécurité', points: 15, icon: 'MessageCircle', hint: 'Posez au moins une question au chatbot.' },
  { id: 'charter-read', label: 'Expert RGPD', points: 25, icon: 'ScrollText', hint: 'Faites défiler au moins 60 % de la charte.' },
  { id: 'certificate-downloaded', label: 'Certifié RGPD', points: 30, icon: 'FileCheck2', hint: 'Téléchargez votre attestation PDF.' },
];

const MASTER_THRESHOLD = 75; // sum of all base badges = 80. So master unlocks near the end.

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { points: 0, unlocked: {}, consent: null };
    const parsed = JSON.parse(raw);
    return { points: parsed.points || 0, unlocked: parsed.unlocked || {}, consent: parsed.consent ?? null };
  } catch (e) {
    return { points: 0, unlocked: {}, consent: null };
  }
}

function writeState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    // Notify all mounted useRGPDGamification hooks in this tab.
    window.dispatchEvent(new CustomEvent('fdn-rgpd-state', { detail: state }));
  } catch (e) {
    // private mode / quota — non-fatal
    if (process.env.NODE_ENV === 'development') console.warn('[RGPDGamification] écriture localStorage impossible', e);
  }
}

export function useRGPDGamification() {
  const [state, setState] = useState(readState);

  // Cross-tab sync (storage event) + same-tab sync (custom event)
  useEffect(() => {
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setState(readState()); };
    const onLocal = (e) => { setState(e.detail || readState()); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('fdn-rgpd-state', onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('fdn-rgpd-state', onLocal);
    };
  }, []);

  const unlock = useCallback((badgeId) => {
    setState((prev) => {
      if (prev.unlocked[badgeId]) return prev;
      const badge = BADGES.find((b) => b.id === badgeId);
      if (!badge) return prev;
      const next = {
        ...prev,
        points: prev.points + badge.points,
        unlocked: { ...prev.unlocked, [badgeId]: { unlockedAt: Date.now() } },
      };
      writeState(next);
      return next;
    });
  }, []);

  const setConsent = useCallback((value) => {
    setState((prev) => {
      const next = { ...prev, consent: { value, at: Date.now() } };
      writeState(next);
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    writeState({ points: 0, unlocked: {}, consent: null });
    setState({ points: 0, unlocked: {}, consent: null });
  }, []);

  const isMaster = state.points >= MASTER_THRESHOLD;
  const progress = Math.min(100, Math.round((state.points / MASTER_THRESHOLD) * 100));

  return { state, unlock, setConsent, resetAll, isMaster, progress, MASTER_THRESHOLD };
}
